import React, { useMemo } from "react";

import { useMutation, useQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Modal,
  TextField,
  Typography,
  Autocomplete,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { addDays, differenceInDays } from "date-fns";
import { Controller, useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import * as yup from "yup";

import client from "@/client";
import { ClientRecipient } from "@/components/ShareFormPanel/ClientListAutoComplete";
import { graphql } from "@/gql";
import { DuplicateProgramInput } from "@/gql/graphql";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { formatISODate, parseDate } from "@/utils/datetime";
import {
  displayError,
  getValidationErrors,
  isGraphqlError,
  isInvalidError,
  parseInvalidError,
} from "@/utils/errors";
import { maybeCall } from "@/utils/functions";
import { FormValues, FragmentType } from "@/utils/type";

interface Props {
  open: boolean;
  onClose?: () => void;
  program: FragmentType<typeof duplicateDialogFragment>;
}

const duplicateDialogFragment = graphql(`
  fragment ProgramDuplicateDialog on Program {
    id
    startDate
    endDate
  }
`);

const useStyles = makeStyles()((theme) => ({
  modalBody: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    margin: theme.spacing(24, "auto"),
    width: 768,
    backgroundColor: colors.white,
    overflowY: "auto",
    padding: theme.spacing(2, 2),
  },
  modalHeaderText: {
    fontSize: "28px",
    fontWeight: "bold",
  },
  modalHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    margin: theme.spacing(2, 2, 0, 2),
  },
  modalContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(8),
    marginBottom: theme.spacing(1),
  },
  saveButton: {
    marginLeft: "18px",
    backgroundColor: colors.primary,
    borderRadius: "32px",
    paddingInline: "24px",
    marginBottom: theme.spacing(7),
  },
  datePicker: {
    marginTop: "8px",
    marginBottom: "8px",
  },
  caption: {
    fontSize: "12px",
  },
  icon: {
    color: colors.primary,
    marginRight: 8,
    width: 21,
    height: 21,
  },
}));

const validationSchema = yup
  .object({
    clientId: yup.string().required("Client is required"),
    startDate: yup.date().required("Start date is required"),
    endDate: yup.date().required("End date is required"),
  })
  .required();

const duplicateDialogQuery = graphql(`
  query DuplicateDialog {
    clients {
      nodes {
        id
        profile {
          id
          fullName
        }
      }
    }
  }
`);

const duplicateProgramMutation = graphql(`
  mutation DuplicateProgram($input: DuplicateProgramInput!) {
    duplicateProgram(input: $input) {
      program {
        id
        client {
          id
          status
        }
      }
    }
  }
`);

const DuplicateDialog: React.FC<Props> = ({ open, onClose, program }) => {
  const { classes } = useStyles();
  const snackbar = useGlobalSnackbar();
  const { data } = useQuery(duplicateDialogQuery);
  const [_duplicateProgram] = useMutation(duplicateProgramMutation);
  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      clientId: "",
      startDate: parseDate(program.startDate),
      endDate: parseDate(program.endDate),
    },
    resolver: yupResolver(validationSchema),
    shouldUnregister: true,
  });

  const clientRecipients = useMemo<ClientRecipient[]>(
    () =>
      data?.clients.nodes
        .map((n) => ({ client: n }))
        .sort((a, b) => {
          return (a.client.profile?.fullName ?? "").localeCompare(
            b.client.profile?.fullName ?? ""
          );
        }) ?? [],
    [data]
  );

  function handleStartDateChange(newDate: Date | null) {
    if (newDate == null) return;
    const { startDate, endDate } = getValues();
    const differenceDays = differenceInDays(endDate, startDate);
    setValue("startDate", newDate);
    setValue("endDate", addDays(newDate, differenceDays));
  }

  async function duplicateProgram(values: FormValues<typeof handleSubmit>) {
    const input: DuplicateProgramInput = {
      id: program.id,
      clientId: values.clientId,
      startDate: formatISODate(values.startDate),
      endDate: formatISODate(values.endDate),
    };
    try {
      await _duplicateProgram({
        variables: { input },
      });
      maybeCall(onClose);
    } catch (e) {
      const invalidError = parseInvalidError(e);
      if (invalidError) {
        const errors = getValidationErrors(invalidError);
        errors.forEach((error) => {
          switch (error.attribute) {
            case "client":
              setError("clientId", { message: error.fullMessage });
              break;
            case "startDate":
              setError("startDate", { message: error.fullMessage });
              break;
            case "endDate":
              setError("endDate", { message: error.fullMessage });
              break;
            case "base":
              snackbar.openSnackBar(error.fullMessage ?? "");
              break;
          }
        });
      }

      snackbar.openSnackBar(displayError(e));
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={classes.modalBody}>
        <Box className={classes.modalHeader}>
          <Typography className={classes.modalHeaderText}>
            Duplicate this plan to another client
          </Typography>
          <IconButton onClick={onClose} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
        <div>
          <form onSubmit={handleSubmit(duplicateProgram)}>
            <Box className={classes.modalContent}>
              <Controller
                name="clientId"
                control={control}
                render={({ field }) => {
                  return (
                    <Autocomplete
                      options={clientRecipients}
                      getOptionLabel={(option: ClientRecipient) => {
                        return option.client?.profile?.fullName ?? "";
                      }}
                      onChange={(_, value) => {
                        field.onChange(value?.client?.id ?? "");
                      }}
                      renderInput={(props) => (
                        <TextField
                          {...props}
                          {...register("clientId")}
                          error={!!errors.clientId}
                          helperText={errors.clientId?.message}
                          label="Client"
                        />
                      )}
                      fullWidth
                      autoComplete
                    />
                  );
                }}
              />
              <Box mb={6} />
              <Grid container>
                <Grid item xs={6}>
                  <Controller
                    control={control}
                    name="startDate"
                    render={({
                      field: { ref, name, ...field },
                      fieldState,
                    }) => (
                      <DatePicker
                        {...field}
                        inputRef={ref}
                        label="Start date"
                        onChange={(value) =>
                          handleStartDateChange(value as Date)
                        }
                        slotProps={{
                          textField: {
                            name,
                            fullWidth: true,
                            error: !!fieldState?.error,
                            helperText: fieldState?.error?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    control={control}
                    name="endDate"
                    render={({
                      field: { ref, name, onChange, ...field },
                      fieldState,
                    }) => (
                      <DatePicker
                        {...field}
                        inputRef={ref}
                        label="End date"
                        onChange={(value) => onChange(value as Date)}
                        slotProps={{
                          textField: {
                            name,
                            fullWidth: true,
                            error: !!fieldState?.error,
                            helperText: fieldState?.error?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Box mb={4} />
              <Button type="submit" disabled={isSubmitting}>
                Save
              </Button>
              <Box mb={5} />
              <Typography
                className={classes.caption}
                variant="body1"
                color="textSecondary"
              >
                Heads up! Updating the start date also adjusts the end date.
                Some meal information may be lost when the duration of the plan
                changes.
              </Typography>
            </Box>
          </form>
        </div>
      </Box>
    </Modal>
  );
};

export default DuplicateDialog;
